/**
 * Édition d'un niveau (`GET|PATCH /admin/levels/:id`).
 *
 * Champs éditables : PV du parcours, éclats de départ (facultatif) et **composition des vagues**
 * (nombre, délais, intervalles, boss). Le parcours et les emplacements sont affichés en lecture
 * seule : leur géométrie est revérifiée par le serveur (segments orthogonaux, grille 32 × 18) et
 * n'est pas un réglage d'équilibrage.
 *
 * L'identifiant n'est jamais envoyé dans le patch : il est immuable (DECISIONS §3). Les index de
 * vague sont réindexés à l'ajout et au retrait, pour qu'ils restent croissants et sans trou —
 * c'est la règle du schéma `vagueSchema`.
 */
import { niveauSchema } from '@elemora/shared/content';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { lireNiveau, modifierNiveau } from '../api/admin';
import { ErreurApi, erreursDeValidation, versErreurApi } from '../api/erreurs';
import { libelleContenu } from '../catalogue/contenu-fr';
import { M } from '../catalogue/messages';
import { useChargement } from '../hooks/use-chargement';
import { Bandeau, BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Champ } from '../ui/Champ';
import { Chargement } from '../ui/Chargement';
import { Coquille } from '../ui/Coquille';
import { Panneau } from '../ui/Panneau';

import type { AdminLevelPatch } from '@elemora/shared/api';
import type { GroupeVague, Niveau, Vague } from '@elemora/shared/content';

/** Saisie du formulaire : nombres en chaînes, vagues en structure. */
interface SaisieNiveau {
  readonly pvParcours: string;
  readonly eclatsDepart: string;
  readonly vagues: readonly Vague[];
}

function saisieDe(niveau: Niveau): SaisieNiveau {
  return {
    pvParcours: String(niveau.pvParcours),
    eclatsDepart: niveau.eclatsDepart === undefined ? '' : String(niveau.eclatsDepart),
    vagues: niveau.vagues.map((vague) => ({
      ...vague,
      groupes: vague.groupes.map((groupe) => ({ ...groupe })),
    })),
  };
}

function nombre(valeur: string): number {
  const converti = Number(valeur);
  return Number.isFinite(converti) ? converti : 0;
}

/** Réindexe les vagues : index 0-based croissants, sans trou (règle du schéma). */
function reindexer(vagues: readonly Vague[]): Vague[] {
  return vagues.map((vague, index) => ({ ...vague, index }));
}

/** Groupe de vague par défaut : un ennemi de base, immédiat, une seconde d'intervalle. */
function groupeParDefaut(ennemiId: string): GroupeVague {
  return { ennemiId, nombre: 1, delaiMs: 0, intervalleMs: 1000 };
}

/** Construit le patch des seuls champs modifiés. */
function construirePatch(initial: Niveau, saisie: SaisieNiveau): AdminLevelPatch {
  const patch: Record<string, unknown> = {};
  const pv = Math.trunc(nombre(saisie.pvParcours));
  if (pv !== initial.pvParcours) patch['pvParcours'] = pv;
  const eclats = saisie.eclatsDepart.trim() === '' ? null : Math.trunc(nombre(saisie.eclatsDepart));
  if (eclats !== null && eclats !== initial.eclatsDepart) patch['eclatsDepart'] = eclats;
  if (JSON.stringify(saisie.vagues) !== JSON.stringify(initial.vagues)) {
    patch['vagues'] = reindexer(saisie.vagues);
  }
  return patch as AdminLevelPatch;
}

export function EcranNiveauEdition() {
  const parametres = useParams<{ id: string }>();
  const identifiant = parametres.id ?? '';
  const chargement = useChargement((signal) => lireNiveau(identifiant, signal));

  const [saisie, setSaisie] = useState<SaisieNiveau | null>(null);
  const [reference, setReference] = useState<Niveau | null>(null);
  const [refus, setRefus] = useState<ErreurApi | null>(null);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (chargement.donnees === null) return;
    setReference(chargement.donnees.level);
    setSaisie(saisieDe(chargement.donnees.level));
    setErreurs({});
    setRefus(null);
  }, [chargement.donnees]);

  /** Ennemi proposé par défaut : le premier du niveau, ou `enn_base` à défaut. */
  const ennemiParDefaut =
    reference?.vagues.flatMap((vague) => vague.groupes)[0]?.ennemiId ?? 'enn_base';

  function majVagues(vagues: readonly Vague[]): void {
    if (saisie === null) return;
    setSaisie({ ...saisie, vagues: reindexer(vagues) });
  }

  async function enregistrer(): Promise<void> {
    if (enCours || reference === null || saisie === null) return;
    setSucces(null);
    setRefus(null);
    setErreurs({});

    const patch = construirePatch(reference, saisie);
    if (Object.keys(patch).length === 0) {
      setSucces(M.cartes.aucuneModification);
      return;
    }

    const candidat: Niveau = {
      ...reference,
      pvParcours: Math.trunc(nombre(saisie.pvParcours)),
      vagues: reindexer(saisie.vagues),
      ...(saisie.eclatsDepart.trim() === ''
        ? {}
        : { eclatsDepart: Math.trunc(nombre(saisie.eclatsDepart)) }),
    };
    const verification = niveauSchema.safeParse(candidat);
    if (!verification.success) {
      const parChemin: Record<string, string> = {};
      for (const probleme of verification.error.issues) {
        parChemin[probleme.path.join('.')] = probleme.message;
      }
      setErreurs(parChemin);
      setRefus(
        new ErreurApi({
          code: 'VALIDATION_FAILED',
          message: M.cartes.validationLocale,
          statutHttp: 0,
          messageServeur: M.cartes.validationLocale,
        }),
      );
      return;
    }

    setEnCours(true);
    try {
      const resultat = await modifierNiveau(identifiant, patch);
      setReference(resultat.level);
      setSaisie(saisieDe(resultat.level));
      setSucces(M.niveaux.enregistre);
    } catch (inconnu) {
      const erreurApi = versErreurApi(inconnu);
      setRefus(erreurApi);
      const parChemin: Record<string, string> = {};
      for (const defaut of erreursDeValidation(erreurApi)) {
        parChemin[defaut.chemin.replace(/^(body|query)\./, '')] = defaut.message;
      }
      setErreurs(parChemin);
    } finally {
      setEnCours(false);
    }
  }

  const introuvable = chargement.erreur?.code === 'NOT_FOUND';
  const titre =
    chargement.donnees === null
      ? M.niveaux.editionTitre(identifiant)
      : libelleContenu(chargement.donnees.level.nameKey);

  return (
    <Coquille
      titre={titre}
      sousTitre={M.niveaux.editionTitre(identifiant)}
      actions={
        <Link className="lien-action" to="/niveaux">
          {M.commun.retourListe}
        </Link>
      }
    >
      <Panneau aide={M.avertissements.brouillon}>
        {introuvable ? (
          <Bandeau texte={M.niveaux.introuvable(identifiant)} ton="avertissement" />
        ) : null}
        {chargement.erreur === null || introuvable ? null : (
          <BandeauErreur erreur={chargement.erreur} surReessai={chargement.recharger} />
        )}
        {chargement.chargement ? <Chargement texte={M.commun.chargementFiche} /> : null}

        {saisie === null || reference === null ? null : (
          <form
            className="formulaire"
            onSubmit={(evenement) => {
              evenement.preventDefault();
              void enregistrer();
            }}
          >
            {refus === null ? null : <BandeauErreur erreur={refus} />}
            {succes === null ? null : <Bandeau texte={succes} ton="succes" />}

            <div className="formulaire__grille">
              <Champ
                id="niveau-pv"
                etiquette={M.niveaux.pvParcours}
                type="number"
                min="1"
                step="1"
                value={saisie.pvParcours}
                aide={`pvParcours — ${M.equilibrage.bornes.entierPositif}`}
                erreur={erreurs['pvParcours'] ?? null}
                onChange={(evenement) =>
                  setSaisie({ ...saisie, pvParcours: evenement.target.value })
                }
              />
              <Champ
                id="niveau-eclats"
                etiquette={M.niveaux.eclatsDepart}
                type="number"
                min="0"
                step="1"
                value={saisie.eclatsDepart}
                aide={M.niveaux.eclatsDepartAide}
                erreur={erreurs['eclatsDepart'] ?? null}
                onChange={(evenement) =>
                  setSaisie({ ...saisie, eclatsDepart: evenement.target.value })
                }
              />
            </div>

            <p className="formulaire__note">{M.avertissements.idsImmuables}</p>

            <fieldset className="formulaire__groupe">
              <legend>{M.niveaux.vaguesTitre}</legend>
              <p className="panneau__aide">{M.niveaux.referencesAide}</p>

              {saisie.vagues.map((vague, indexVague) => (
                <div className="vague" key={`vague-${String(indexVague)}`}>
                  <div className="vague__entete">
                    <h3 className="vague__titre">{M.niveaux.vague(indexVague)}</h3>
                    <label className="case">
                      <input
                        type="checkbox"
                        checked={vague.boss}
                        onChange={(evenement) => {
                          const vagues = saisie.vagues.map((courante, rang) =>
                            rang === indexVague
                              ? { ...courante, boss: evenement.target.checked }
                              : courante,
                          );
                          majVagues(vagues);
                        }}
                      />
                      {M.niveaux.boss}
                    </label>
                    <Bouton
                      variante="discret"
                      onClick={() =>
                        majVagues(saisie.vagues.filter((_courante, rang) => rang !== indexVague))
                      }
                      disabled={saisie.vagues.length <= 1}
                    >
                      {M.niveaux.supprimerVague}
                    </Bouton>
                  </div>

                  <table className="tableau tableau--compact">
                    <thead>
                      <tr>
                        <th scope="col">{M.niveaux.ennemiId}</th>
                        <th scope="col">{M.niveaux.nombre}</th>
                        <th scope="col">{M.niveaux.delaiMs}</th>
                        <th scope="col">{M.niveaux.intervalleMs}</th>
                        <th scope="col">{M.commun.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vague.groupes.map((groupe, indexGroupe) => {
                        const majGroupe = (suivant: GroupeVague): void => {
                          const vagues = saisie.vagues.map((courante, rang) =>
                            rang === indexVague
                              ? {
                                  ...courante,
                                  groupes: courante.groupes.map((candidat, rangGroupe) =>
                                    rangGroupe === indexGroupe ? suivant : candidat,
                                  ),
                                }
                              : courante,
                          );
                          majVagues(vagues);
                        };
                        return (
                          <tr key={`groupe-${String(indexVague)}-${String(indexGroupe)}`}>
                            <td>
                              <input
                                className="champ__saisie"
                                aria-label={M.niveaux.ennemiId}
                                value={groupe.ennemiId}
                                onChange={(evenement) =>
                                  majGroupe({ ...groupe, ennemiId: evenement.target.value })
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="champ__saisie"
                                aria-label={M.niveaux.nombre}
                                type="number"
                                min="1"
                                step="1"
                                value={String(groupe.nombre)}
                                onChange={(evenement) =>
                                  majGroupe({
                                    ...groupe,
                                    nombre: Math.trunc(nombre(evenement.target.value)),
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="champ__saisie"
                                aria-label={M.niveaux.delaiMs}
                                type="number"
                                min="0"
                                step="1"
                                value={String(groupe.delaiMs)}
                                onChange={(evenement) =>
                                  majGroupe({
                                    ...groupe,
                                    delaiMs: Math.trunc(nombre(evenement.target.value)),
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="champ__saisie"
                                aria-label={M.niveaux.intervalleMs}
                                type="number"
                                min="0"
                                step="1"
                                value={String(groupe.intervalleMs)}
                                onChange={(evenement) =>
                                  majGroupe({
                                    ...groupe,
                                    intervalleMs: Math.trunc(nombre(evenement.target.value)),
                                  })
                                }
                              />
                            </td>
                            <td>
                              <Bouton
                                variante="discret"
                                onClick={() => {
                                  const vagues = saisie.vagues.map((courante, rang) =>
                                    rang === indexVague
                                      ? {
                                          ...courante,
                                          groupes: courante.groupes.filter(
                                            (_candidat, rangGroupe) => rangGroupe !== indexGroupe,
                                          ),
                                        }
                                      : courante,
                                  );
                                  majVagues(vagues);
                                }}
                                disabled={vague.groupes.length <= 1}
                              >
                                {M.niveaux.supprimerGroupe}
                              </Bouton>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <Bouton
                    variante="secondaire"
                    onClick={() => {
                      const vagues = saisie.vagues.map((courante, rang) =>
                        rang === indexVague
                          ? {
                              ...courante,
                              groupes: [...courante.groupes, groupeParDefaut(ennemiParDefaut)],
                            }
                          : courante,
                      );
                      majVagues(vagues);
                    }}
                  >
                    {M.niveaux.ajouterGroupe}
                  </Bouton>
                </div>
              ))}

              <Bouton
                variante="secondaire"
                onClick={() =>
                  majVagues([
                    ...saisie.vagues,
                    {
                      index: saisie.vagues.length,
                      boss: false,
                      groupes: [groupeParDefaut(ennemiParDefaut)],
                    },
                  ])
                }
              >
                {M.niveaux.ajouterVague}
              </Bouton>
            </fieldset>

            <fieldset className="formulaire__groupe">
              <legend>{M.niveaux.parcoursTitre}</legend>
              <p className="panneau__aide">{M.niveaux.parcoursAide}</p>
              <p className="formulaire__note">
                {M.niveaux.noeuds(reference.parcours.length)} ·{' '}
                {M.niveaux.emplacements(reference.emplacements.length)} ·{' '}
                {reference.grille.colonnes} × {reference.grille.lignes}
              </p>
              <p className="formulaire__note">
                {reference.parcours
                  .map((noeud) => `(${String(noeud.x)},${String(noeud.y)})`)
                  .join(' → ')}
              </p>
            </fieldset>

            <div className="formulaire__actions">
              <Bouton type="submit" enCours={enCours} texteEnCours={M.commun.enregistrementEnCours}>
                {M.commun.enregistrer}
              </Bouton>
              <button
                type="button"
                className="lien-action"
                onClick={() => setSaisie(saisieDe(reference))}
              >
                {M.commun.annuler}
              </button>
            </div>
          </form>
        )}
      </Panneau>
    </Coquille>
  );
}
